import { memo } from 'react';
import {
  haveSameCallbacks,
  isDependencyMapReference,
  nodeHasComponentIdInReferences,
  referenceEqualWith,
} from './viewComponentEquivalence';
import { isEqualWith } from 'lodash';
import {
  DependencyMapNodeViewModel,
  DependencyMapReferenceOrParent,
  DependencyMapViewNodeReferencesProperties,
} from './types';
import { ReferenceDirection } from '@ardoq/api-types';

const areReferencesEqual = (
  a: DependencyMapReferenceOrParent[] | null,
  b: DependencyMapReferenceOrParent[] | null
) => a === b || isEqualWith(a, b, referenceEqualWith);

const isComponentChangeAffectingReferences = (
  node: DependencyMapNodeViewModel,
  oldComponentId: string | null,
  newComponentId: string | null
) => {
  const hasPreviousComponent = nodeHasComponentIdInReferences(
    node,
    oldComponentId
  );
  const hasNewComponent = nodeHasComponentIdInReferences(node, newComponentId);
  return hasPreviousComponent || hasNewComponent;
};

const areViewNodeReferencesEffectivelyEqual = (
  oldProps: DependencyMapViewNodeReferencesProperties,
  newProps: DependencyMapViewNodeReferencesProperties
) => {
  if (
    oldProps.node.id !== newProps.node.id ||
    oldProps.colorByReference !== newProps.colorByReference ||
    !haveSameCallbacks(oldProps, newProps) ||
    !areReferencesEqual(
      oldProps.node.incomingReferences,
      newProps.node.incomingReferences
    ) ||
    !areReferencesEqual(
      oldProps.node.outgoingReferences,
      newProps.node.outgoingReferences
    ) ||
    !areReferencesEqual(
      oldProps.node.combinedReferences,
      newProps.node.combinedReferences
    )
  ) {
    return false;
  }

  const { node } = newProps;

  if (
    oldProps.hoveredComponentId !== newProps.hoveredComponentId &&
    isComponentChangeAffectingReferences(
      node,
      oldProps.hoveredComponentId,
      newProps.hoveredComponentId
    )
  ) {
    return false;
  }

  if (
    oldProps.focusedComponentId !== newProps.focusedComponentId &&
    isComponentChangeAffectingReferences(
      node,
      oldProps.focusedComponentId,
      newProps.focusedComponentId
    )
  ) {
    return false;
  }

  if (
    oldProps.selectedComponentId !== newProps.selectedComponentId &&
    isComponentChangeAffectingReferences(
      node,
      oldProps.selectedComponentId,
      newProps.selectedComponentId
    )
  ) {
    return false;
  }

  return true;
};

const getViewNodeProperties = (reference: DependencyMapReferenceOrParent) =>
  isDependencyMapReference(reference)
    ? {
        headerLabel: reference.label,
        otherLabels: reference.otherLabels,
        isCollapsedReference: reference.isCollapsedReference,
        node: reference.node,
        referenceIds: reference.referenceIds,
      }
    : {
        headerLabel: null,
        otherLabels: undefined,
        isCollapsedReference: false,
        node: reference,
        referenceIds: null,
      };

const DependencyMapViewNodeReferences = memo(
  ({
    viewInstanceId,
    node: { id, incomingReferences, outgoingReferences, combinedReferences },
    colorByReference,
    selectedComponentId,
    hoveredComponentId,
    focusedComponentId,
    setHoveredComponentId,
    setFocusedComponentId,
    onComponentClick,
    onReferenceClick,
    onComponentDoubleClick,
    onUrlIconClick,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    contextMenuDataAttribute,
    isNewConditionalFormatting,
    DependencyMap,
  }: DependencyMapViewNodeReferencesProperties) => (
    <>
      {(combinedReferences?.length
        ? [
            {
              references: combinedReferences,
              referenceDirection: ReferenceDirection.UNSET,
            },
          ]
        : [
            {
              references: incomingReferences,
              referenceDirection: ReferenceDirection.INCOMING,
            },
            {
              references: outgoingReferences,
              referenceDirection: ReferenceDirection.OUTGOING,
            },
          ]
      ).map(({ references, referenceDirection }) =>
        references?.map((reference, index) => {
          const {
            headerLabel,
            node,
            isCollapsedReference,
            referenceIds,
            otherLabels,
          } = getViewNodeProperties(reference);
          return (
            <DependencyMap.ViewNode
              key={`${id}-${referenceDirection}-${index}`}
              node={node}
              viewInstanceId={viewInstanceId}
              headerLabel={headerLabel}
              headerSubLabels={otherLabels}
              subLabel={null}
              colorByReference={colorByReference}
              className={
                colorByReference ? reference.className : node.className
              }
              iconColor={
                colorByReference ? reference.iconColor : node.iconColor
              }
              fill={colorByReference ? reference.fill : node.fill}
              stroke={colorByReference ? reference.stroke : node.stroke}
              textColor={
                colorByReference ? reference.textColor : node.textColor
              }
              textModerateColor={
                colorByReference
                  ? reference.textModerateColor
                  : node.textModerateColor
              }
              referenceDirection={referenceDirection}
              selectedComponentId={selectedComponentId}
              hoveredComponentId={hoveredComponentId}
              focusedComponentId={focusedComponentId}
              setHoveredComponentId={setHoveredComponentId}
              setFocusedComponentId={setFocusedComponentId}
              showVertical={false}
              isRoot={false}
              isCollapsedReference={isCollapsedReference}
              onComponentClick={onComponentClick}
              onReferenceClick={onReferenceClick}
              onComponentDoubleClick={onComponentDoubleClick}
              onUrlIconClick={onUrlIconClick}
              urlFieldValuesByComponentId={urlFieldValuesByComponentId}
              urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
              referenceIds={referenceIds}
              siblingCount={
                (incomingReferences?.length ?? 0) +
                (outgoingReferences?.length ?? 0) -
                1
              }
              contextMenuDataAttribute={contextMenuDataAttribute}
              isNewConditionalFormatting={isNewConditionalFormatting}
              DependencyMap={DependencyMap}
            />
          );
        })
      )}
    </>
  ),
  areViewNodeReferencesEffectivelyEqual
);
export { DependencyMapViewNodeReferences };
